import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Search, Menu, X, Store, User, LogOut, Sun, Moon } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useTheme } from '../../context/ThemeContext';
import LanguageSwitcher from '../common/LanguageSwitcher';

const StoreHeader = () => {
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/store/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ type: "spring", stiffness: 100, damping: 20 }}
      className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border-b border-gray-200/50 dark:border-gray-700/50 sticky top-0 z-50"
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <Link to="/store" className="hover:scale-105 transition-transform shrink-0">
              {isDarkMode ? (
                <img src="/sttora.png" alt="STTORA" className="h-10" />
              ) : (
                <div className="inline-block p-2 bg-gray-800 rounded-xl">
                  <img src="/sttora.png" alt="STTORA" className="h-9" />
                </div>
              )}
            </Link>
            <nav className="hidden md:flex items-center gap-6 ml-8">
              <Link to="/store/categories" className="text-sm text-gray-600 dark:text-gray-300 hover:text-orange-600 relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 hover:after:w-full after:bg-orange-500 after:transition-all after:duration-300">
                Categorías
              </Link>
              <Link to="/store/featured" className="text-sm text-gray-600 dark:text-gray-300 hover:text-orange-600 relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 hover:after:w-full after:bg-orange-500 after:transition-all after:duration-300">
                Destacados
              </Link>
              <Link to="/store/packs" className="text-sm text-gray-600 dark:text-gray-300 hover:text-orange-600 relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 hover:after:w-full after:bg-purple-500 after:transition-all after:duration-300">
                Packs
              </Link>
            </nav>
          </div>

          <form onSubmit={handleSearch} className="hidden md:flex items-center flex-1 max-w-md mx-4">
            <div className="relative w-full">
              <input
                type="text"
                placeholder="Buscar productos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
              />
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            </div>
          </form>

          <div className="flex items-center gap-1">
            <LanguageSwitcher />
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={toggleTheme}
              className="p-2 rounded-xl text-gray-600 dark:text-gray-300 hover:text-orange-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              {isDarkMode ? <Sun className="w-5 h-5 text-yellow-400" /> : <Moon className="w-5 h-5" />}
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => navigate('/store/cart')}
              className="relative p-2 text-gray-600 dark:text-gray-300 hover:text-orange-600"
            >
              <ShoppingCart className="w-5 h-5" />
              {cart.item_count > 0 && (
                <motion.span
                  key={cart.item_count}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-1 -right-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center shadow-sm"
                >
                  {cart.item_count}
                </motion.span>
              )}
            </motion.button>
            {user ? (
              <div className="relative group">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  className="flex items-center gap-2 p-2 text-gray-600 dark:text-gray-300 hover:text-orange-600"
                >
                  <User className="w-5 h-5" />
                  <span className="hidden sm:block text-sm font-medium">{user.first_name}</span>
                </motion.button>
                <AnimatePresence>
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-xl border dark:border-gray-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all py-1">
                    {user.role === 'admin' && (
                      <Link to="/admin" className="block px-4 py-2.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-orange-600 transition-colors">Panel Admin</Link>
                    )}
                    <Link to="/profile" className="block px-4 py-2.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-orange-600 transition-colors">Mi Perfil</Link>
                    <Link to="/orders" className="block px-4 py-2.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-orange-600 transition-colors">Mis Órdenes</Link>
                    <hr className="my-1 dark:border-gray-700" />
                    <button onClick={logout} className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center gap-2 transition-colors">
                      <LogOut className="w-4 h-4" /> Cerrar Sesión
                    </button>
                  </div>
                </AnimatePresence>
              </div>
            ) : (
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  to="/login"
                  className="text-sm bg-gradient-to-r from-orange-500 to-orange-600 text-white px-5 py-2 rounded-xl hover:shadow-lg hover:shadow-orange-500/25 transition-all"
                >
                  Iniciar Sesión
                </Link>
              </motion.div>
            )}
            <motion.button
              whileTap={{ scale: 0.9 }}
              className="md:hidden p-2 text-gray-600 dark:text-gray-300"
              onClick={() => setShowMenu(!showMenu)}
            >
              {showMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </motion.button>
          </div>
        </div>

        <AnimatePresence>
          {showMenu && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="md:hidden overflow-hidden"
            >
              <div className="pb-4 border-t border-gray-200 dark:border-gray-700">
                <form onSubmit={handleSearch} className="mt-3">
                  <input
                    type="text"
                    placeholder="Buscar productos..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700"
                  />
                </form>
                <nav className="mt-3 space-y-1">
                  <Link to="/store/categories" className="block px-4 py-2.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl" onClick={() => setShowMenu(false)}>Categorías</Link>
                  <Link to="/store/featured" className="block px-4 py-2.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl" onClick={() => setShowMenu(false)}>Destacados</Link>
                  <Link to="/store/packs" className="block px-4 py-2.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl" onClick={() => setShowMenu(false)}>Packs</Link>
                </nav>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default StoreHeader;
